import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CartProvider } from './contexts/CartContext';
import { SavedProductsProvider } from './contexts/SavedProductsContext';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import GenderGate from './components/GenderGate';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Profile from './pages/Profile';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Shops from './pages/Shops';
import ShopDetail from './pages/ShopDetail';
import Branches from './pages/Branches';
import Artists from './pages/Artists';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Booking from './pages/Booking';
import Cart from './pages/Cart';
import Confirm from './pages/Confirm';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminBookings from './pages/Admin/Bookings';
import AdminProducts from './pages/Admin/Products';
import AdminUsers from './pages/Admin/Users';
import AdminShops from './pages/Admin/Shops';
import AdminTimeSlots from './pages/Admin/TimeSlots';
import AdminServices from './pages/Admin/Services';
import AdminBundles from './pages/Admin/Bundles';
import AdminSeats from './pages/Admin/Seats';
import AdminOrders from './pages/Admin/Orders';
import AdminFinancialReport from './pages/Admin/FinancialReport';
import ManagerDashboard from './pages/Manager/Dashboard';
import ManagerArtists from './pages/Manager/Artists';
import ManagerProducts from './pages/Manager/Products';
import ManagerBookings from './pages/Manager/Bookings';
import ManagerReservations from './pages/Manager/Reservations';
import ManagerAnalytics from './pages/Manager/Analytics';
import ManagerServices from './pages/Manager/Services';
import ManagerBundles from './pages/Manager/Bundles';
import ManagerSeats from './pages/Manager/Seats';
import ManagerBranches from './pages/Manager/Branches';
import ManagerAttendance from './pages/Manager/Attendance';
import ManagerFinancialReport from './pages/Manager/FinancialReport';
import UserAnalytics from './pages/UserAnalytics';
import Payment from './pages/Payment';
import PaymentResult from './pages/PaymentResult';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import PremiumHome from './pages/Premium/PremiumHome';
import BookPriorityWindow from './pages/Premium/BookPriorityWindow';
import MyWindows from './pages/Premium/MyWindows';
import OnMyWay from './pages/Premium/OnMyWay';
import PremiumMembershipPage from './pages/Premium/PremiumMembership';
import AdminPremiumRequests from './pages/Admin/PremiumRequests';
import AdminPremiumUsers from './pages/Admin/PremiumUsers';
import AdminSalonPremiumSettings from './pages/Admin/SalonPremiumSettings';
import AdminVIPCalendar from './pages/Admin/VIPCalendar';
import AdminArtists from './pages/Admin/Artists';
import AdminAttendance from './pages/Admin/Attendance';
import AdminHandoffProtocol from './pages/Admin/HandoffProtocol';
import ManagerVIPRequests from './pages/Manager/VIPRequests';
import ManagerVIPMembers from './pages/Manager/VIPMembers';
import ManagerVIPCalendar from './pages/Manager/VIPCalendar';
import ManagerHandoffProtocol from './pages/Manager/HandoffProtocol';
import WorkerHome from './pages/Worker/Home';
import WorkerSchedule from './pages/Worker/Schedule';

function AppContent() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const requiresGenderSelection = user && (user.gender === null || user.gender === undefined);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        {t('common.loading')}
      </div>
    );
  }
  if (requiresGenderSelection) {
    return <GenderGate />;
  }

  return (
    <ThemeProvider>
      <NotificationProvider>
        <SavedProductsProvider>
          <CartProvider>
            <Router>
            <ScrollToTop />
            <Layout>
              <ErrorBoundary>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/shops" element={<Shops />} />
                <Route path="/shop/:shopId" element={<ShopDetail />} />
                <Route path="/branches" element={<Branches />} />
                <Route path="/artists" element={<Artists />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />

                {/* Protected User Routes */}
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/booking" element={<ProtectedRoute><Booking /></ProtectedRoute>} />
                <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
                <Route path="/confirm" element={<ProtectedRoute><Confirm /></ProtectedRoute>} />
                <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
                <Route path="/payment-result" element={<PaymentResult />} />
                <Route path="/my-analytics" element={<UserAnalytics />} />

                {/* Premium VIP Routes */}
                <Route path="/premium" element={<ProtectedRoute><PremiumHome /></ProtectedRoute>} />
                <Route path="/premium/book" element={<ProtectedRoute><BookPriorityWindow /></ProtectedRoute>} />
                <Route path="/premium/windows" element={<ProtectedRoute><MyWindows /></ProtectedRoute>} />
                <Route path="/premium/on-my-way/:bookingId" element={<ProtectedRoute><OnMyWay /></ProtectedRoute>} />
                <Route path="/premium/membership" element={<ProtectedRoute><PremiumMembershipPage /></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute requireSuperAdmin><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requireSuperAdmin><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/shops" element={<ProtectedRoute requireSuperAdmin><AdminShops /></ProtectedRoute>} />
                <Route path="/admin/bookings" element={<ProtectedRoute requireSuperAdmin><AdminBookings /></ProtectedRoute>} />
                <Route path="/admin/artists" element={<ProtectedRoute requireSuperAdmin><AdminArtists /></ProtectedRoute>} />
                <Route path="/admin/attendance" element={<ProtectedRoute requireSuperAdmin><AdminAttendance /></ProtectedRoute>} />
                <Route path="/admin/handoff-protocol" element={<ProtectedRoute requireSuperAdmin><AdminHandoffProtocol /></ProtectedRoute>} />
                <Route path="/admin/products" element={<ProtectedRoute requireSuperAdmin><AdminProducts /></ProtectedRoute>} />
                <Route path="/admin/time-slots" element={<ProtectedRoute requireSuperAdmin><AdminTimeSlots /></ProtectedRoute>} />
                <Route path="/admin/services" element={<ProtectedRoute requireSuperAdmin><AdminServices /></ProtectedRoute>} />
                <Route path="/admin/bundles" element={<ProtectedRoute requireSuperAdmin><AdminBundles /></ProtectedRoute>} />
                <Route path="/admin/seats" element={<ProtectedRoute requireSuperAdmin><AdminSeats /></ProtectedRoute>} />
                <Route path="/admin/orders" element={<ProtectedRoute requireSuperAdmin><AdminOrders /></ProtectedRoute>} />
                <Route path="/admin/financial-report" element={<ProtectedRoute requireSuperAdmin><AdminFinancialReport /></ProtectedRoute>} />
                <Route path="/admin/premium-requests" element={<ProtectedRoute requireSuperAdmin><AdminPremiumRequests /></ProtectedRoute>} />
                <Route path="/admin/premium-users" element={<ProtectedRoute requireSuperAdmin><AdminPremiumUsers /></ProtectedRoute>} />
                <Route path="/admin/premium-settings" element={<ProtectedRoute requireSuperAdmin><AdminSalonPremiumSettings /></ProtectedRoute>} />
                <Route path="/admin/vip-calendar" element={<ProtectedRoute requireSuperAdmin><AdminVIPCalendar /></ProtectedRoute>} />

                {/* Manager Routes */}
                <Route path="/manager" element={<ProtectedRoute requireSalonManager><ManagerDashboard /></ProtectedRoute>} />
                <Route path="/manager/artists" element={<ProtectedRoute requireSalonManager><ManagerArtists /></ProtectedRoute>} />
                <Route path="/manager/bookings" element={<ProtectedRoute requireSalonManager><ManagerBookings /></ProtectedRoute>} />
                <Route path="/manager/reservations" element={<ProtectedRoute requireSalonManager><ManagerReservations /></ProtectedRoute>} />
                <Route path="/manager/products" element={<ProtectedRoute requireSalonManager><ManagerProducts /></ProtectedRoute>} />
                <Route path="/manager/analytics" element={<ProtectedRoute requireSalonManager><ManagerAnalytics /></ProtectedRoute>} />
                <Route path="/manager/services" element={<ProtectedRoute requireSalonManager><ManagerServices /></ProtectedRoute>} />
                <Route path="/manager/bundles" element={<ProtectedRoute requireSalonManager><ManagerBundles /></ProtectedRoute>} />
                <Route path="/manager/seats" element={<ProtectedRoute requireSalonManager><ManagerSeats /></ProtectedRoute>} />
                <Route path="/manager/branches" element={<ProtectedRoute requireSalonManager><ManagerBranches /></ProtectedRoute>} />
                <Route path="/manager/attendance" element={<ProtectedRoute requireSalonManager><ManagerAttendance /></ProtectedRoute>} />
                <Route path="/manager/financial-report" element={<ProtectedRoute requireSalonManager><ManagerFinancialReport /></ProtectedRoute>} />
                <Route path="/manager/vip-requests" element={<ProtectedRoute requireSalonManager><ManagerVIPRequests /></ProtectedRoute>} />
                <Route path="/manager/vip-members" element={<ProtectedRoute requireSalonManager><ManagerVIPMembers /></ProtectedRoute>} />
                <Route path="/manager/vip-calendar" element={<ProtectedRoute requireSalonManager><ManagerVIPCalendar /></ProtectedRoute>} />
                <Route path="/manager/handoff-protocol" element={<ProtectedRoute requireSalonManager><ManagerHandoffProtocol /></ProtectedRoute>} />

                {/* Worker */}
                <Route path="/worker" element={<WorkerHome />} />
                <Route path="/worker/schedule" element={<WorkerSchedule />} />

                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/contact" element={<Contact />} />

                {/* 404 Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </ErrorBoundary>
            </Layout>
            </Router>
          </CartProvider>
        </SavedProductsProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
